import { cn } from "@/lib/utils";
import SectionHeading from "./SectionHeading";

const features = ["Self-healing leases", "Built-in audit trail", "Privacy-first"];

type Status = "claimed" | "progress" | "requeued" | "open";

const statusClass: Record<Status, string> = {
  claimed: "text-accent-text bg-secondary",
  progress: "text-[color:var(--ok)] bg-[var(--ok-bg)]",
  requeued: "text-[color:var(--warn)] bg-[var(--warn-bg)]",
  open: "text-muted-foreground bg-transparent",
};

const tasks: { id: string; title: string; label: string; status: Status; who: string; worker: "human" | "agent" | "open"; initials?: string }[] = [
  { id: "TD-204", title: "Migrate session store to Redis", label: "claimed", status: "claimed", who: "dev-1 · developer", worker: "human", initials: "D1" },
  { id: "TD-206", title: "Add retries to webhook worker", label: "in progress", status: "progress", who: "agent-1 · Claude Code", worker: "agent" },
  { id: "TD-208", title: "Write integration tests for billing", label: "reclaimed", status: "requeued", who: "agent-2", worker: "agent" },
  { id: "TD-211", title: "Review API rate-limit design", label: "claimed", status: "claimed", who: "dev-2 · developer", worker: "human", initials: "D2" },
  { id: "TD-212", title: "Update CLI docs", label: "open", status: "open", who: "", worker: "open" },
];

const BacklogIllustration = () => (
  <div
    role="img"
    aria-label="Illustration: humans and agents claiming tasks from a shared backlog"
    className="relative w-full overflow-hidden rounded-2xl border border-border bg-card shadow-glow"
  >
    <div className="flex items-center justify-between gap-3 border-b border-border px-[18px] py-3.5 font-mono text-xs text-muted-foreground">
      <span className="font-medium text-foreground">backlog</span>
      <span className="flex items-center gap-3">
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full border border-primary-light bg-secondary" />
          human
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-[3px] bg-grad-primary" />
          agent
        </span>
      </span>
    </div>
    {tasks.map((t) => (
      <div
        key={t.id}
        className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3.5 gap-y-1.5 border-b border-border px-[18px] py-3.5"
      >
        <div className="min-w-0">
          <div className="font-mono text-[11px] text-muted-foreground">{t.id}</div>
          <div className="mt-[3px] truncate text-sm font-medium">{t.title}</div>
        </div>
        <div className="flex items-center gap-2">
          <span className={cn("whitespace-nowrap rounded-md px-2 py-[3px] font-mono text-[11px]", statusClass[t.status])}>
            {t.label}
          </span>
          {t.worker === "human" && (
            <span
              title={t.who}
              className="grid h-7 w-7 place-items-center rounded-full border border-primary-light bg-secondary text-[11px] font-semibold text-accent-text"
            >
              {t.initials}
            </span>
          )}
          {t.worker === "agent" && (
            <span
              title={t.who}
              className="grid h-7 w-7 place-items-center rounded-lg bg-grad-primary font-mono text-[10px] font-semibold text-white"
            >
              &gt;_
            </span>
          )}
          {t.worker === "open" && <span className="h-7 w-7 rounded-full border border-dashed border-border" />}
        </div>
      </div>
    ))}
    <div className="flex flex-col gap-0.5 px-[18px] py-3 font-mono text-[11px] leading-[1.6] text-muted-foreground">
      <span>
        <span className="text-[color:var(--warn)]">●</span> lease expired on TD-208 · returned to queue
      </span>
      <span>
        <span className="text-[color:var(--ok)]">●</span> agent-2 claimed TD-208 · logged to audit trail
      </span>
    </div>
  </div>
);

const ProductsSection = () => (
  <section
    id="products"
    className="mx-auto max-w-[1200px] scroll-mt-[68px] px-[clamp(16px,5vw,48px)] pt-[clamp(80px,10vw,140px)]"
  >
    <SectionHeading index="02" label="Products">
      What we're building
    </SectionHeading>

    <article className="mt-12 flex flex-wrap overflow-hidden rounded-3xl border border-border bg-card shadow-primary">
      <div className="flex min-w-0 flex-[1_1_400px] flex-col p-[clamp(24px,5vw,56px)]">
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-mono text-xs text-muted-foreground">P/01</span>
          <span className="text-[22px] font-semibold tracking-[-0.02em]">TaskDuck</span>
          <span className="rounded-full border border-primary-light bg-secondary px-2.5 py-[5px] font-mono text-[11px] uppercase tracking-[0.06em] text-accent-text">
            Coming soon
          </span>
        </div>
        <h3 className="text-balance mt-7 text-[clamp(26px,3.2vw,40px)] font-semibold leading-[1.1] tracking-[-0.03em]">
          The coordination layer for software teams working with AI coding agents.
        </h3>
        <p className="text-pretty mt-[18px] max-w-[520px] text-[17px] leading-[1.65] text-muted-foreground">
          Your team writes the backlog. Developers and AI agents claim tasks from it, and no two workers ever take the
          same task.
        </p>
        <ul className="mt-7 grid list-none grid-cols-[repeat(auto-fit,minmax(min(100%,180px),1fr))] gap-x-6 gap-y-2.5 p-0">
          {features.map((f) => (
            <li key={f} className="flex items-center gap-2.5 text-[15px] font-medium">
              <span className="h-[7px] w-[7px] flex-none rotate-45 bg-grad-primary" aria-hidden="true" />
              {f}
            </li>
          ))}
        </ul>
        <dl className="mt-8 grid grid-cols-[auto_minmax(0,1fr)] gap-x-5 gap-y-3 border-t border-border pt-6 text-[15px]">
          <dt className="pt-0.5 font-mono text-xs uppercase tracking-[0.06em] text-muted-foreground">Agents</dt>
          <dd className="m-0">Connect through MCP, for example Claude Code</dd>
          <dt className="pt-0.5 font-mono text-xs uppercase tracking-[0.06em] text-muted-foreground">People</dt>
          <dd className="m-0">CLI, web app, or the Mac menu-bar app</dd>
        </dl>
        <a
          href="https://taskduck.com"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-8 inline-flex items-center gap-2 self-start font-mono text-sm font-medium text-accent-text hover:text-foreground"
        >
          Visit taskduck.com
          <span aria-hidden="true">↗</span>
        </a>
      </div>

      <div className="relative flex min-w-0 flex-[1_1_420px] items-center overflow-hidden border-l border-border bg-[var(--viz-bg)] p-[clamp(20px,4vw,48px)]">
        <div className="grid-motif pointer-events-none absolute inset-0 bg-[length:32px_32px]" aria-hidden="true" />
        <BacklogIllustration />
      </div>
    </article>

    <div className="mt-4 flex flex-wrap items-center justify-between gap-x-6 gap-y-3 rounded-2xl border border-dashed border-border px-7 py-6">
      <div className="flex items-center gap-3.5">
        <span className="font-mono text-xs text-muted-foreground">P/02</span>
        <span className="text-[17px] font-medium">More in the lab</span>
      </div>
      <span className="font-mono text-[13px] text-muted-foreground">Not ready to show yet.</span>
    </div>
  </section>
);

export default ProductsSection;
